/**
 * DraggableColumnItem Component
 *
 * Individual column item in the available columns list.
 * Can be dragged to Row Groups or Values drop zones.
 */

import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { TableFieldApiResponse } from '@loader/shared-types';
import { GripVertical, Hash, Type, Calendar, CheckSquare, Clock, Pencil, Copy, Trash2 } from 'lucide-react';
import { Checkbox, Button } from '@loader/ui';

/**
 * Props for DraggableColumnItem
 */
interface DraggableColumnItemProps {
  /** Field information */
  field: TableFieldApiResponse;
  /** Qualified key for drag/toggle (e.g., "alias:fieldId"). Falls back to field.id */
  fieldKey?: string;
  /** Whether the field is in use (in row_groups or values) */
  isChecked: boolean;
  /** Handler to toggle field (remove from row_groups/values when unchecked) */
  onToggle: (fieldId: string, isChecked: boolean) => void;
  /** Whether drag is disabled (e.g., when field is already in drop zones) */
  disabled?: boolean;
  /** Whether the user can edit (viewers cannot) */
  canEdit?: boolean;
  /** Handler for editing (only shown for calculated columns) */
  onEdit?: (fieldId: string) => void;
  /** Handler for duplicating (only shown for calculated columns) */
  onDuplicate?: (fieldId: string) => void;
  /** Handler for deleting (only shown for calculated columns) */
  onDelete?: (fieldId: string) => void;
}

/**
 * Get icon for field data type
 */
function getFieldTypeIcon(dataType: string) {
  switch (dataType) {
    case 'number':
      return Hash;
    case 'text':
      return Type;
    case 'date':
      return Calendar;
    case 'datetime':
      return Clock;
    case 'boolean':
      return CheckSquare;
    default:
      return Type;
  }
}

/**
 * DraggableColumnItem Component
 */
export function DraggableColumnItem({
  field,
  fieldKey,
  isChecked,
  onToggle,
  disabled = false,
  canEdit = true,
  onEdit,
  onDuplicate,
  onDelete,
}: DraggableColumnItemProps) {
  const resolvedKey = fieldKey ?? field.id;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: resolvedKey,
    disabled: disabled || !canEdit,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  const displayName = field.name;
  const TypeIcon = getFieldTypeIcon(field.data_type);
  const hasActions = onEdit || onDuplicate || onDelete;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center gap-2 py-1 px-1
        ${disabled ? 'cursor-default' : isDragging ? 'cursor-grabbing' : 'cursor-grab'}
      `}
      {...attributes}
      {...listeners}
    >
      {/* Checkbox - checked when field is in row_groups or values */}
      <Checkbox
        checked={isChecked}
        onCheckedChange={() => onToggle(resolvedKey, isChecked)}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        disabled={!canEdit}
      />

      {/* Drag Handle */}
      <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />

      {/* Field Type Icon */}
      <TypeIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />

      {/* Field Name */}
      <span className="flex-1 text-sm truncate" title={displayName}>
        {displayName}
      </span>

      {/* Edit/Delete Actions (for calculated columns) */}
      {hasActions && (
        <div className="flex items-center gap-0.5 shrink-0">
          {onEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full text-muted-foreground"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(field.id);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label={`Edit ${displayName} column`}
            >
              <Pencil className="size-3" />
            </Button>
          )}
          {onDuplicate && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full text-muted-foreground"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(field.id);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label={`Duplicate ${displayName} column`}
            >
              <Copy className="size-3" />
            </Button>
          )}
          {onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(field.id);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label={`Delete ${displayName} column`}
            >
              <Trash2 className="size-3" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
